<template>
  <TirPmContextMenu
    class="context-menu"
    ref="$contextMenu"
    min-width="20rem"
    maxHeight="max-content"
    :trigger-buttons="[TirPmContextMenuMouseButtonEnum.Right]"
  >
    <RouterLink
      ref="$subtask"
      class="task-subtask-view"
      :to="computedRoute"
      @mousedown.left="openTaskInDrawer(subtask.Id)"
      @mousedown.middle.prevent
    >
      <div class="task-subtask-view__status" :style="{ backgroundColor: subtask.Status.Color }" />
      <div class="task-subtask-view__taskid">{{ subtask.Id }}</div>
      <TaskParameter
        v-if="statusParam"
        :type="TaskParameterTypeEnum.Status"
        :data="statusParam"
        preview
        hide-remove
      />
      <div class="task-subtask-view__name">
        {{ subtask.Name }}
      </div>
      <div class="task-subtask-view__performers">
        <TirPmTooltipElement v-for="performer in performerVisible" width="max-content">
          <TirPmIconView
            class="task-subtask-view__performer"
            :size="24"
            :image-load-url="performer.ImageUrl"
            :color-code="performer.AvatarColor"
            is-rounded
            :key="performer.Id"
          >
            <template #mainSlot>
              {{ computedName(performer) }}
            </template>
          </TirPmIconView>
          <template #content>
            <div class="task-subtask-view__hint">
              {{ performer.LastName }} {{ performer.FirstName }} {{ performer.MiddleName }}
            </div>
          </template>
        </TirPmTooltipElement>
        <TirPmIconView
          v-if="hiddenCountPerformers > 0"
          class="task-subtask-view__performer"
          :size="24"
          is-rounded
        >
          <template #mainSlot>
            <div class="task-subtask-view__performers-hidden-count">
              +{{ hiddenCountPerformers }}
            </div>
          </template>
        </TirPmIconView>
      </div>
      <TirPmTooltipElement class="tir-pm-task-card_popover">
        <template #default>
          <ListBulletIcon class="task-subtask-view__queue-icon" @click.stop />
        </template>
        <template #content>
          <div class="task-subtask-view__list">
            <RouterLink
              v-for="(queue, idx) in subtask.Queues"
              v-text="queue.Name"
              :key="idx"
              class="task-subtask-view__list__item"
              :to="goToQueue(queue)"
              target="_blank"
            />
          </div>
        </template>
      </TirPmTooltipElement>
      <TirPmTooltipElement>
        <FlagIcon
          class="task-subtask-view__priority-icon"
          :style="{ color: subtask.Priority.Color }"
        />
        <template #content>
          <div class="task-subtask-view__hint">
            <div v-if="subtask.Priority.Name">
              {{ subtask.Priority.Name }}
            </div>
            <div v-else class="task-subtask-view__absence-data">-</div>
          </div>
        </template>
      </TirPmTooltipElement>
      <div style="margin-left: auto">
        <div @click.stop.prevent @mousedown.stop.prevent>
          <TirPmPopover
            ref="$confirmUnattachPopover"
            :custom-position="{
              right: '0rem',
              bottom: '0.25rem',
            }"
          >
            <TirPmButton
              v-if="editing"
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Small"
              :is-loading="isLoadingUnattachButton"
            >
              <template #leftSlot>
                <TrashIcon />
              </template>
            </TirPmButton>
            <template #content>
              <div class="task-subtask-view__confirm-popover">
                <span class="task-subtask-view__confirm-popover-message">
                  Открепить подзадачу?
                </span>
                <div class="task-subtask-view__confirm-popover-buttons">
                  <TirPmButton
                    :variant="TirPmButtonVariantEnum.PrimaryState"
                    :size="TirPmButtonSizeEnum.Small"
                    :is-loading="isLoadingUnattachButton"
                    @click.stop="emit('onClickDelete')"
                  >
                    Открепить
                  </TirPmButton>
                  <TirPmButton
                    :variant="TirPmButtonVariantEnum.SecondaryState"
                    :size="TirPmButtonSizeEnum.Small"
                    :is-disabled="isLoadingUnattachButton"
                    @click.stop="$confirmUnattachPopover?.close()"
                  >
                    Отмена
                  </TirPmButton>
                </div>
              </div>
            </template>
          </TirPmPopover>
        </div>
      </div>
    </RouterLink>
    <template #content>
      <RouterLink :to="computedRoute" target="_blank">
        <TirPmContextMenuButton>
          <div class="task-subtask-view__menu-item">
            <ArrowsPointingOutIcon class="task-subtask-view__menu-icon" />
            Открыть в новой вкладке
          </div>
        </TirPmContextMenuButton>
      </RouterLink>
      <RouterLink :to="openSchema">
        <TirPmContextMenuButton>
          <div class="task-subtask-view__menu-item">
            <MapIcon class="task-subtask-view__menu-icon" />
            Посмотреть карту проекта
          </div>
        </TirPmContextMenuButton>
      </RouterLink>
      <TirPmContextMenuButton @click="copyNameAndLinkTask">
        <div class="task-subtask-view__menu-item">
          <ShareIcon class="task-subtask-view__menu-icon" />
          Скопировать название и ссылку
        </div>
      </TirPmContextMenuButton>
    </template>
  </TirPmContextMenu>
  <TaskDrawer
    ref="$taskDrawer"
    @on-update-task="emit('updatedSubtask', subtask.Id)"
    @removeSubtaskItem="emit('onClickDelete')"
  />
</template>

<script lang="ts" setup>
import { computed, inject, PropType, Ref, ref, watch } from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmPopover,
  PopoverPositionEnum,
  PopoverColorEnum,
  TirPmContextMenu,
  TirPmContextMenuMouseButtonEnum,
  TirPmContextMenuButton,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmTooltipElement,
} from 'tir-components';
import {
  ArrowsPointingOutIcon,
  CurtainIcon,
  ListBulletIcon,
  MapIcon,
  ShareIcon,
  TrashIcon,
} from 'tir-style-system/icons/outline';
import { FlagIcon } from 'tir-style-system/icons/solid';
import { QueueRelationModel, SubtaskModel, UserModel } from '../../models';
import { RouterLink, useRouter } from 'vue-router';
import TaskDrawer from '../task-drawer/task-drawer.vue';
import { TaskDrawerSettingsModel } from '../task-drawer';
import { TaskParameter, TaskParameterModel, TaskParameterTypeEnum } from '@/components';
import { copyToClipboard } from '../../utils';
import { TaskModel } from '../task-drawer/models';

/**Маршруты */
const router = useRouter();

/**Параметры */
const props = defineProps({
  /**id проекта */
  projectId: { type: Number, required: true },
  /**id очереди */
  queueId: { type: String },
  /**Подзадача */
  subtask: {
    type: Object as PropType<SubtaskModel>,
    required: true,
  },
  /**Кол-во видимых исполнителей */
  countVisiblePerformers: {
    type: Number,
    default: 4,
  },
  /**Загрузка при откреплении задачи */
  isLoadingUnattachButton: {
    type: Boolean,
    default: false,
  },

  editing: { type: Boolean, default: true },
});
/**События */
const emit = defineEmits<{
  /**Событие клика на кнопку удаления */
  (e: 'onClickDelete'): void;
  /**Событие обновления подзадачи */
  (e: 'updatedSubtask', subtaskId: number): void;
}>();

const $confirmUnattachPopover = ref<InstanceType<typeof TirPmPopover>>();
/**Ссылка на компонент контекстного меню */
const $contextMenu = ref<InstanceType<typeof TirPmContextMenu>>();
/**Ссылка на компонент шторки задачи */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();
/**Ссылка на DOM */
const $subtask = ref<HTMLElement>();
/**Отображать схему подзадач */
const showSchema = ref(false);

const performerVisible = computed(() =>
  props.subtask.Performers.slice(0, props.countVisiblePerformers),
);
const hiddenCountPerformers = computed(
  () => props.subtask.Performers.length - performerVisible.value.length,
);

const statusParam = computed(
  () =>
    props.subtask.Status &&
    new TaskParameterModel({
      Id: props.subtask.Status.Id,
      Name: props.subtask.Status.Name,
      Color: props.subtask.Status.Color,
    }),
);

const computedName = (user: UserModel) => {
  return user.FirstName?.[0]?.toUpperCase() + user.LastName?.[0]?.toUpperCase();
};

const openTaskInDrawer = (subtaskId: number) => {
  $contextMenu.value?.close();
  openDrawer(subtaskId);
};

/**Открыть задачу */
const openDrawer = (subtaskId: number) => {
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: subtaskId,
    }),
  );
};
/**Перейти в очередь */
const goToQueue = (queue: QueueRelationModel) => {
  return router.resolve({
    name: 'queue',
    params: {
      projectId: props.projectId,
      namespaceId: queue.Namespace.Id,
      queueId: queue.Id,
    },
  })?.href;
};
/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};

/**Скопировать название и ссылку */
const copyNameAndLinkTask = async () => {
  if (!props.projectId || !props.queueId) return;

  const route = router.resolve({
    name: 'task-detail',
    params: {
      projectId: props.projectId,
      queueId: props.queueId,
      taskId: props.subtask.Id,
    },
  });

  if (!route?.href) return;
  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + route.href;
  const textContent = `${props.subtask.Id} ${props.subtask.Name}`;

  if (await copyToClipboard(`[${textContent}](${link})`)) {
    $contextMenu.value.close();
    addNotification('Название и ссылка тикета скопирована');
    console.log(10);
  }
};

/**Переход в задачу */
const computedRoute = computed(() => {
  return router.resolve({
    name: 'task-detail',
    params: {
      taskId: props.subtask.Id,
      projectId: props.projectId,
      namespaceId: props.subtask.Queues[0].Namespace.Id,
      queueId: props.queueId,
    },
  })?.href;
});
/**Открыть карту проекта */
const openSchema = computed(() => {
  return router.resolve({
    name: 'project-map',
    params: {
      taskId: props.subtask.Id,
      projectId: props.projectId,
    },
  });
});
defineExpose({
  subtaskItem: $subtask,
});
</script>

<style lang="scss" scoped>
.task-subtask-view {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.438rem 0.625rem 0.438rem 0.625rem;
  border: 0.063rem solid var(--neutral-translucent-10, #272d371a);
  border-radius: 0.5rem;
  gap: 1.5rem;
  color: var(--text-primary, #272d37);
  cursor: pointer;

  &__menu-icon {
    width: 1.5rem;
    min-width: 1.5rem;
    height: 1.5rem;
  }

  &__task-negative {
    color: var(--text-negative);
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__absence-data {
    font-size: 1.2rem;
  }

  &__hint {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-wrap: break-word;
  }

  &__queue-icon {
    width: 1rem;
    height: 1rem;
    color: var(--text-secondary);
    transition: 0.2s linear;
    &:hover {
      color: #2a77ef;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    overflow-wrap: break-word;
    &__item {
      display: inline-block;
      max-width: 100%;
      padding: 0.3125rem;
      text-align: center;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: 0.2s linear;
      &:hover {
        color: #2a77ef;
      }
    }
  }

  &__taskid {
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    color: var(--text-primary, #272d37);
  }

  &__status {
    position: absolute;
    left: -0.063rem;
    top: 0;
    bottom: 0;
    width: 0.375rem;
    border-top-left-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;
  }
  &__name {
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__priority {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5.063rem;
    height: 2.125rem;
    flex-shrink: 0;
  }
  &__priority-icon {
    width: 1rem;
    height: 1rem;
  }
  &__performers {
    display: flex;
    align-items: center;
  }
  &__performer {
    &:not(:first-child) {
      margin-left: -0.125rem;
    }
  }
  &__performers-hidden-count {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    text-align: center;
    color: var(--text-primary, #272d37);
  }
  &__performer-name {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    text-align: center;
    color: var(--text-primary, #272d37);
  }
  &__confirm-popover {
    border-radius: 0.5rem;
    background: var(--bg-base, #fff);
    padding: var(--12, 0.75rem);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  &__confirm-popover-message {
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5rem;
  }
  &__confirm-popover-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>

<style lang="scss">
.tir-pm-context-menu__content {
  z-index: 100000 !important;
}
</style>
